// src/services/api/apiSlice.ts

import { createApi } from '@reduxjs/toolkit/query/react';
import { dynamicBaseQuery } from './baseQuery';

/**
 * How long RTK Query keeps a query's data after its last subscriber unmounts.
 *
 * Every query in this app reads dashboard aggregates or master lists that are
 * rebuilt by a single nightly batch job — the data does not change at any point
 * during the day. The RTK default of 60 SECONDS therefore threw away still-valid
 * data constantly: leaving a tab for a minute and coming back triggered a full
 * refetch of numbers that cannot have changed.
 *
 * 6 hours comfortably covers a working session, which is all this needs to do —
 * it only measures idle time after the last subscriber unmounts, so anything
 * beyond a session length adds nothing.
 *
 * This value is a memory knob, NOT a correctness control. Staleness across the
 * nightly rollover is handled separately by the data-version stamp (see
 * `useDataVersion`): dashboard queries carry the stamp in their args and
 * `useDashboardCacheRollover` clears the cache when a new generation appears, so
 * a new batch can never be served from old entries regardless of this number.
 *
 * Note: this only affects QUERIES. Login / SSO are mutations and unaffected.
 */
export const DASHBOARD_CACHE_SECONDS = 60 * 60 * 6; // 6 hours

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: dynamicBaseQuery,
  tagTypes: ['Auth', 'Dashboard', 'TLM', 'Student', 'School', 'Report'],
  // Keep data for a whole session instead of refetching every 60s.
  keepUnusedDataFor: DASHBOARD_CACHE_SECONDS,
  endpoints: () => ({}),
});
